import { Link } from 'react-router-dom';
import { useStore } from '../lib/store';
import { usePageMeta } from '../lib/meta';
import { EmptyState, PageHeading } from '../components/ui';

/**
 * Static content pages.
 *
 * Copy is carried over from the current storefront's policy pages so the
 * replacement launches with the same commitments. These live in code rather
 * than the database because they change rarely and benefit from review; if
 * Gillian wants to edit them herself, they can move into the settings
 * container behind a rich-text field in admin.
 */

export function CollectionsIndexPage() {
  const { data } = useStore();
  usePageMeta({ title: 'Collections | Gill’s Handmade' });

  const groups: Array<[string, string]> = [
    ['earrings', 'Earrings'],
    ['necklaces', 'Necklaces'],
    ['bracelets', 'Bracelets'],
    ['featured', 'Featured collections'],
    ['sale', 'Sale'],
  ];

  return (
    <div className="page-container py-10">
      <PageHeading
        title="Collections"
        description="Browse by type, by colour, or by the collections Gillian has put together."
      />

      <div className="mt-10 space-y-12">
        {groups.map(([group, label]) => {
          const categories = (data?.categories ?? []).filter((c) => c.menuGroup === group);
          if (categories.length === 0) return null;

          return (
            <section key={group}>
              <h2 className="mb-5 text-xs uppercase tracking-widest text-muted">{label}</h2>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {categories.map((category) => (
                  <Link
                    key={category.id}
                    to={`/collections/${category.slug}`}
                    className="group border border-border p-6 transition-colors hover:border-ink"
                  >
                    <h3 className="font-heading text-lg leading-snug">{category.title}</h3>
                    {category.description && (
                      <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-muted">
                        {category.description}
                      </p>
                    )}
                    <p className="mt-4 text-xs uppercase tracking-widest text-muted group-hover:text-ink">
                      {category.productCount} pieces →
                    </p>
                  </Link>
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}

export function AboutPage() {
  usePageMeta({
    title: 'About | Gill’s Handmade',
    description:
      'Handmade jewellery by Gillian, made with artisan ceramic pieces and Czech glass beads.',
  });

  return (
    <div className="page-container max-w-2xl py-12">
      <PageHeading title="About" />
      <div className="rich-text mt-8 text-sm">
        <p>
          Every piece here is made by hand, one at a time, using artisan ceramic
          components and Czech glass beads. Most designs are one of a kind — when a piece
          sells, that exact combination of beads and colours is gone.
        </p>
        <p>
          The work is inspired by the natural world: flowers and blossoms, leaves, birds,
          and the colours of a garden through the seasons. Pieces are wire-wrapped and
          assembled by hand, so no two are ever quite identical.
        </p>
        <p>
          Orders are wrapped in a rustic linen pouch or a recycled kraft gift box,
          finished with Japanese washi tape and twine, and protected for the post.
        </p>
      </div>
    </div>
  );
}

export function ShippingPage() {
  usePageMeta({
    title: 'Shipping & Store Policies | Gill’s Handmade',
    description: 'Delivery, returns and store policies.',
  });

  return (
    <div className="page-container max-w-2xl py-12">
      <PageHeading title="Shipping &amp; store policies" />
      <div className="rich-text mt-8 text-sm">
        <h2>Delivery</h2>
        <p>
          UK orders are sent free of charge by Royal Mail Tracked 24. Orders are
          dispatched within 3 working days of cleared payment.
        </p>

        <h2>Returns</h2>
        <p>
          Returns are accepted within 14 days of receipt for a full refund excluding the
          original postage, provided items are unused and in their original packaging.
        </p>
        <p>
          For hygiene reasons, earrings cannot be returned. Custom pieces are not
          eligible for return.
        </p>
        <p>
          Postage is refunded only where merchandise is faulty or damaged. Non-delivery
          must be reported within 21 days.
        </p>

        <h2>Payment</h2>
        <p>
          Full payment is required at the time of purchase and must clear before an order
          is dispatched.
        </p>
      </div>
    </div>
  );
}

export function PrivacyPage() {
  usePageMeta({ title: 'Privacy Policy | Gill’s Handmade' });

  return (
    <div className="page-container max-w-2xl py-12">
      <PageHeading title="Privacy policy" />
      <div className="rich-text mt-8 text-sm">
        <h2>What we collect</h2>
        <p>
          To fulfil an order we collect your name, delivery address, email address and,
          if you provide one, a phone number. If you create an account, sign-in is handled
          by Microsoft Entra External ID and we never see or store your password.
        </p>

        <h2>How it is used</h2>
        <p>
          Your details are used to process and deliver your order and to contact you
          about it. We do not sell your information, and we do not share it beyond what
          is needed to take payment and post your parcel.
        </p>

        <h2>Payments</h2>
        <p>
          Card details are handled entirely by our payment provider. They are never sent
          to or stored on this website.
        </p>

        <h2>Your rights</h2>
        <p>
          You can ask for a copy of the information we hold about you, or ask us to
          correct or delete it, by getting in touch.
        </p>
      </div>
    </div>
  );
}

export function NotFoundPage() {
  usePageMeta({ title: 'Page not found' });
  return (
    <EmptyState
      title="We could not find that page"
      message="The link may be out of date, or the piece may have sold."
      action={
        <Link to="/collections/all-jewellery" className="text-sm underline underline-offset-4">
          Browse all jewellery
        </Link>
      }
    />
  );
}
